import {computed, observable} from "mobx";

import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {BaseLinkModel} from "../../../infrastructure/models/link/base-link-model";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DASHBOARD} from "../../../containers/dashboard-container/dashboard-container.consts";
import {ScoresListHeaderModuleMobileDateViewModel} from "./scores-list-header-module.mobile-date.view-model";
import {ScoresListHeaderModuleConfig} from "../scores-list-header-module.config";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {SportDomainModel} from "../../../domain/sports/models/sport-domain-model";
import {ODDS_TYPES} from "../../../core/components/odds/components/odds-consts";
import {DashboardLinkModel} from "../../../containers/dashboard-container/models/dashboard-container-link-model";
import {ROUTE_NAMES} from "../../../core/consts";
import {AllScoresLinkModel} from "../../../containers/all-scores-container/models/all-scores-container-link-model";
import {HREF_SPORT_TYPES} from "../../../core/app/consts";
import languages from "../../../core/app/consts/languages";

const NUM_OF_MOBILE_DATES = 10;
const NUM_OF_MOBILE_DATES_VISIBLE = 5;

export class ScoresListHeaderModuleState extends BaseModuleState {
  config: ScoresListHeaderModuleConfig;
  @observable _endDate: Date;
  @observable _date: Date;
  @observable menuOpen: Boolean;
  _isLive: Boolean;
  @observable _days: Array<ScoresListHeaderModuleMobileDateViewModel>;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, ScoresListHeaderModuleConfig];

  _initializeDays() {
    this._days = this.createMobileDaterDates();
  }

  set startDate(date: Date) {
    const dateInRange = this.datetimeUtils.isDateInRange(date, this._days[0].date, this._days[this._days.length - 1].date);
    const maxOffsetFromCenter = NUM_OF_MOBILE_DATES - Math.ceil(this.visibleMobileDates / 2);
    const diffFromCenter = Math.abs(this.datetimeUtils.diffDaysByDate(date, this._days[NUM_OF_MOBILE_DATES].date));

    if (!dateInRange || diffFromCenter > maxOffsetFromCenter) {
      this._days = this.createMobileDaterDates(this.selectedSport?.nameForURL, date);
    }

    this._date = date;
  }

  @computed get startDate(): Date {
    return this._date;
  }

  set endDate(endDate: Date) {
    this._endDate = endDate;
  }

  @computed get endDate(): Date {
    return this._endDate;
  }

  set isLive(isLive: Boolean) {
    this._isLive = isLive;
  }

  @computed get isLive(): Date {
    return this._isLive;
  }

  set linkType(linkType: BaseLinkModel) {
    this._linkType = linkType;
  }

  @computed get linkType(): BaseLinkModel {
    return this._linkType;
  }

  @computed get isToday(): Boolean {
    return this.datetimeUtils.isToday(this.startDate);
  }

  get visibleMobileDates(): Number {
    return NUM_OF_MOBILE_DATES_VISIBLE;
  }

  @computed get mobileCurrentDays(): Array<Object> {
    return this._days;
  }

  get allScoresLinkModel(): BaseLinkModel {
    return new this.linkType(DASHBOARD.FILTERS.all, this.selectedSport?.nameForURL);
  }

  @computed get selectedSport(): SportDomainModel {
    return this.appState.context.sportType;
  }

  get liveScoresLinkModel(): BaseLinkModel {
    const linkModel = new this.linkType(DASHBOARD.FILTERS.live, this.selectedSport?.nameForURL);

    linkModel.replace = true;

    return linkModel;
  }

  @computed get daysOffset(): Number {
    const diffInDays: Number = this.datetimeUtils.diffDaysByDate(this.startDate, this._days[NUM_OF_MOBILE_DATES].date);

    let daysOffset = (Math.round(this._days.length / 2) + diffInDays - Math.ceil(this.visibleMobileDates / 2));

    return daysOffset;
  }

  get mobileDateWidth() {
    return Number(this.device.screenWidth) / (this.visibleMobileDates + 1)
  }

  get showBanner(): Boolean {
    return this.config.adsSettings.showBanner;
  }

  isLinkTag(date: Date): Boolean {
    const diffDays: Number = this.datetimeUtils.diffDaysByDate(date, new Date());
    const onDateRage: Boolean = diffDays <= this.config.nextDaysAmountToBeLink && diffDays >= this.config.prevDaysAmountToBeLink;
    const isMajorSportType: Boolean = HREF_SPORT_TYPES[this.appState.context.sportType?.nameForURL] > 0;
    const isMainLang: Boolean = languages.map(lang => lang.isMain && lang.id).filter(id => !!id).includes(this.language.id)

    const isLinkTag: Boolean = onDateRage && isMajorSportType && isMainLang;

    return isLinkTag;
  }

  get isWidget() {
    return this.appSettings.isWidget;
  }

  createMobileDaterDates(sportName: String = this.appState.context.sportType?.nameForURL, filterDate: Date = this.startDate): Array<ScoresListHeaderModuleMobileDateViewModel> {
    const days: Array<ScoresListHeaderModuleMobileDateViewModel> = [];
    const linkType = this.currentLinkModel?.name === ROUTE_NAMES.allScores ? AllScoresLinkModel : DashboardLinkModel

    for (let i = -NUM_OF_MOBILE_DATES; i <= NUM_OF_MOBILE_DATES; i++) {
      const date: Date = this.datetimeUtils.addDays(filterDate, i);
      const name: String = this.datetimeUtils.toShortNamedDay(date);

      const link = new ScoresListHeaderModuleMobileDateViewModel(date, name, sportName, linkType, this.isLinkTag(date), this.datetimeUtils, this.isWidget);

      days.push(link);

    }

    return days;
  }

  @computed get dates(): Array<ScoresListHeaderModuleMobileDateViewModel> {
    const dates = []

    for (let i = -NUM_OF_MOBILE_DATES; i <= NUM_OF_MOBILE_DATES; i++) {
      const date: Date = this.datetimeUtils.addDays(this.startDate, i);
      const name = this.datetimeUtils.toShortNamedDay(date);

      dates.push(new ScoresListHeaderModuleMobileDateViewModel(date, name, this.selectedSport?.name, BaseLinkModel, false, this.datetimeUtils));
    }

    return dates;
  }

  @computed get liveGamesCount(): Number {
    return this.appState.liveGamesCount;
  }

  @computed get showOdds(): Number {
    return this.appState.showOdds;
  }

  @computed get selectedOddsType(): ODDS_TYPES {
    return this.appState.currentOddsType;
  }

  get isPopulated(): Boolean {
    return true;
  }
}

